import { DocumentFileList } from '@/common/components/DocumentFileList'
import { cn } from '@/common/utils/cn'
import { checkIsAcceptedFile } from '@/common/utils/fileValidator'
import { replacePeriodToUnderscore } from '@/common/utils/transformers'
import { UploadIcon } from 'lucide-react'
import { BaseSyntheticEvent, FormEvent, useId, useState } from 'react'
import { ArrayPath, Control, FieldArrayWithId, FieldValues, useFieldArray } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import Skeleton from '../Skeleton'

export interface MultipleDocumentUploaderProps<T extends FieldValues> extends React.HTMLAttributes<HTMLInputElement> {
  id?: string;
  label?: string
  name?: string
  title?: string
  uploadBlob: (data: { file: File; filename: string }) => Promise<any>
  accept?: string
  className?: string
  fileListClassName?: string;
  fileItemClassName?: string
  containerClassName?: string,
  control: Control<T, any>
  disabled?: boolean
  fieldName: ArrayPath<T>
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  softDelete?: (attachmentId: string) => void
  fieldKey?: string
  dataTestid?: string
  maxFileSizeInMb?: number
  setError?: (errorMessage?: string) => void
  isLoading?: boolean
}

interface UploadedFile {
  fileName: string
  currentFileUrl: string
}

export const MultipleDocumentUploader = <T extends FieldValues>({
  id,
  label,
  name,
  title = 'Anexe um arquivo',
  className,
  containerClassName,
  fileListClassName,
  fileItemClassName,
  uploadBlob,
  accept = '',
  control,
  fieldName,
  submit,
  softDelete,
  fieldKey,
  dataTestid,
  maxFileSizeInMb = 20,
  setError,
  isLoading = false,
  ...props
}: MultipleDocumentUploaderProps<T>) => {
  const uniqueId = useId();

  const [dragActive, setDragActive] = useState(false);

  const {
    fields: files,
    append: appendFile,
    remove: removeFile,
  } = useFieldArray({
    control,
    name: fieldName,
  })

  const handleDocumentsChange = async (e: FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement & { files: FileList }

    let files = target.files.length ? Array.from(target.files)?.filter((file) => checkIsAcceptedFile(file, accept)) : []

    if (maxFileSizeInMb && setError) {
      const sizes = files.map((file) => file.size)

      if (sizes.some((size) => size > maxFileSizeInMb * 1024 * 1024)) {
        target.value = ''

        files = files.filter((file) => file.size <= maxFileSizeInMb * 1024 * 1024)

        setError?.(`Um ou mais arquivos excedem o tamanho de ${maxFileSizeInMb}MB.`)
      } else {
        setError?.(undefined);
      }
    }

    if (!files.length) {
      return
    }

    for (const file of files) {
      const data = await uploadBlob({ file, filename: file.name })

      if (data?.data?.data?.id) {
        appendFile({
          id: data.data.data.id,
          file,
          fileName: file.name,
          currentFileUrl: URL.createObjectURL(file),
        } as unknown as FieldArrayWithId<T, ArrayPath<T>>)

        submit && submit()
      }
    }
    target.value = ''
  }

  const handleRemoveFile = (currentIndex: number) => {
    if (files.length > 0) {
      if (softDelete) {
        const attachmentId = (files[currentIndex] as any)[fieldKey || 'id']

        return softDelete(attachmentId)
      }

      removeFile(currentIndex)
    }
  }

  return (
    <div className={cn("flex flex-wrap gap-4", containerClassName)}>
      {files.length ? (
        <DocumentFileList
          files={files.map((file) => file as unknown as UploadedFile)}
          handleRemoveFile={handleRemoveFile}
          className={fileItemClassName}
          containerClassName={fileListClassName}
          dataTestid={name && replacePeriodToUnderscore(name)}
        />
      ) : null}

      {isLoading && (
        <Skeleton className='w-[180px] h-[100px] rounded-lg' />
      )}

      <div className="flex flex-col items-start">
        {label && (
          <p className="text-xs mb-1">
            {label}
          </p>
        )}

        <label
          htmlFor={id || uniqueId || "file_input"}
          className={twMerge(
            'flex flex-col justify-center border border-neutral-3 w-[180px] h-[100px] gap-2 items-center bg-neutral-1 cursor-pointer rounded-lg',
            className,
            dragActive && "bg-blue-100/30 border-blue-300",
          )}
          onDragLeave={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDragActive(false);
            }
          }}
          onDragEnter={e => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragOver={e => {
            e.preventDefault();
          }}
          onDrop={e => {
            e.preventDefault();
            setDragActive(false);
            handleDocumentsChange({ target: { files: e.dataTransfer.files } } as any);
          }}
        >
          <p className="text-xs text-center">
            {title}
          </p>

          <UploadIcon strokeWidth={1} />

          <p className="text-xs italic">
            {dragActive ? "Solte aqui" : "Ou arraste até aqui"}
          </p>
        </label>

        <input
          type="file"
          name={name}
          data-testid={dataTestid || (name ? replacePeriodToUnderscore(name) + '_file_input' : '')}
          accept={accept}
          className="hidden"
          id={id || uniqueId || "file_input"}
          multiple
          onChange={handleDocumentsChange}
          {...props}
        />
      </div>
    </div>
  )
}
